import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Table, Input, Button, message, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const LanguageList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();

  // State management
  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} languages`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
  });

  // Typing fired one request per keystroke. The term is now searched once the
  // typing settles, on the same 300ms the users and promos lists use.
  const searchDebounceRef = useRef(null);
  const SEARCH_DEBOUNCE_MS = 300;
  // Monotonic request id — responses from superseded requests are dropped so a
  // slow earlier search can't overwrite the results of the latest one.
  const requestSeqRef = useRef(0);

  const getLanguages = useCallback(
    async (params = {}) => {
      const requestId = ++requestSeqRef.current;
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching languages with params:", requestParams);
        const response = await masterService.getVideoLanguages(requestParams);
        if (requestId !== requestSeqRef.current) return; // superseded by a newer request
        console.log("Languages API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const languagesData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Languages data:", languagesData);
          console.log("Pagination data:", paginationData);

          // Ensure languagesData is an array
          if (Array.isArray(languagesData)) {
            setLanguages(languagesData);
          } else {
            console.error("Languages data is not an array:", languagesData);
            setLanguages([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          message.error("Failed to fetch languages");
          setLanguages([]);
        }
      } catch (error) {
        if (requestId !== requestSeqRef.current) return;
        console.error("Error fetching languages:", error);
        message.error("Failed to fetch languages");
        setLanguages([]);
      } finally {
        // Only the newest request clears the spinner — an overtaken one finishing
        // early would otherwise show the list as loaded while it is still loading.
        if (requestId === requestSeqRef.current) setLoading(false);
      }
    },
    [filters.searchText]
  );

  // Load languages data. Mount only: every later fetch is an explicit
  // getLanguages(...) from a search or pagination handler. Re-running this
  // whenever getLanguages is rebuilt would fire a second, redundant request
  // alongside each of them — and one per keystroke, which is what the debounce
  // below exists to stop.
  useEffect(() => {
    setPageTitle("Languages");
    getLanguages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value ?? "";
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      getLanguages({
        searchText: value.trim(),
        currentPage: 1,
      });
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  // Enter key / search icon applies immediately, cancelling any pending debounce
  // so the term is not searched twice.
  const handleSearchNow = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    getLanguages({ searchText: (filters.searchText || "").trim(), currentPage: 1 });
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getLanguages({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add language
  const handleAddLanguage = () => {
    navigate("/languages/add/new");
  };

  // Handle edit language
  const handleEditLanguage = (langId) => {
    navigate(`/languages/edit/${langId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Language Name",
      dataIndex: "lang_name",
      key: "lang_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.lang_name.localeCompare(b.lang_name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "ISO Code",
      dataIndex: "lang_iso_639_2_code",
      key: "lang_iso_639_2_code",
      width: 120,
      render: (text) => (
        <Tag color="blue" style={{ fontSize: "12px" }}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Native Name",
      dataIndex: "native_name",
      key: "native_name",
      render: (text) => <Text style={{ fontSize: "13px" }}>{text}</Text>,
    },
    {
      title: "Allow Translation",
      dataIndex: "is_translation_active",
      key: "is_translation_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Show in Preference",
      dataIndex: "show_in_preference",
      key: "show_in_preference",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Preference Order Number",
      dataIndex: "preference_order_number",
      key: "preference_order_number",
      render: (status) => (
        <Text strong style={{ fontSize: "14px" }}>{status}</Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "lang_is_active",
      key: "lang_is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditLanguage(record.lang_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Video Languages ({pagination.total})
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 300,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Search
              placeholder="Search languages..."
              allowClear
              value={filters.searchText}
              onChange={handleSearchChange}
              onSearch={handleSearchNow}
              style={{ width: 320, maxWidth: "40vw" }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddLanguage}
              size="middle"
            >
              Add Language
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={languages}
          rowKey="lang_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{
            emptyText: "No languages found",
            filterConfirm: "OK",
            filterReset: "Reset",
            filterEmptyText: "No filters",
          }}
        />
      </Card>
  );
};

export default LanguageList;
